import { Pagination, PaginationProps } from './Pagination';
import { bytesToSize, generateId } from '../utils';
import { ReactNode, useMemo, useRef, useState } from 'react';
import {
  CSSObject,
  Flex,
  Icon,
  IconProps,
  Input,
  InputProps,
  Text,
  ThemingProps,
  useMultiStyleConfig,
} from '@chakra-ui/react';
import { AiOutlineCloudUpload } from 'react-icons/ai/index.js';

export interface FileWithExtraProps {
  name: string;
  size: number;
  lastModified: number;
  slice: File['slice'];
  arrayBuffer: File['arrayBuffer'];
  stream: File['stream'];
  text: File['text'];
  webkitRelativePath: string;
  type: string;
  uid?: string;
  percent?: number;
}

export interface DraggerProps {
  children?: ReactNode;
  bordered?: boolean;
  containerProps?: CSSObject;
  files?: FileWithExtraProps[];
  fileProps?: InputProps;
  onFilesAdded?: (files: FileWithExtraProps[]) => void;
  onChange?: (files: FileWithExtraProps[]) => void;
}

export interface FileUploadProps extends DraggerProps {
  uploadIcon?: ReactNode;
  uploadIconProps?: IconProps;
  label?: ReactNode;
  rootProps?: CSSObject;
  showUploadedFiles?: boolean;
  selectedFilesRenderer?: (file: FileWithExtraProps) => ReactNode;
  paginationProps?: PaginationProps;
  children?: ReactNode;
  uploadedFilesContainer?: CSSObject;
  uploadedFilesTitle?: CSSObject;
  totalSizeTextProps?: CSSObject;
  colorTheme?: ThemingProps['colorScheme'];
}

const Dragger = (props: DraggerProps) => {
  const {
    children,
    bordered,
    containerProps,
    files = [],
    fileProps,
    onFilesAdded,
    onChange,
  } = props;
  const styles = useMultiStyleConfig('FileUpload', props);

  const fileRef = useRef<HTMLInputElement>(null);
  const blockEvent = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const updateFileWithExtraProps = (
    files: FileWithExtraProps[],
  ): FileWithExtraProps[] => {
    const toReturn = files.map((file) => {
      file['uid'] = file['uid'] || generateId();
      file['percent'] = file['percent'] || 0;
      return file;
    });
    return toReturn;
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    blockEvent(e);
    onFilesAdded &&
      onFilesAdded(
        updateFileWithExtraProps(Object.values(e.dataTransfer?.files || {})),
      );
    onChange &&
      onChange([
        ...files,
        ...updateFileWithExtraProps(Object.values(e.dataTransfer?.files || {})),
      ]);
  };
  const handleFileSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFilesAdded &&
      onFilesAdded(
        updateFileWithExtraProps(Object.values(e.target?.files || {})),
      );
    onChange &&
      onChange([
        ...files,
        ...updateFileWithExtraProps(Object.values(e.target.files || {})),
      ]);
  };

  return (
    <Flex
      sx={{ ...(bordered ? styles.Container : {}), ...containerProps }}
      onClick={() => fileRef.current?.click()}
      onDragEnter={blockEvent}
      onDragLeave={blockEvent}
      onDragOver={blockEvent}
      onDrop={handleDrop}
    >
      <Input
        type='file'
        ref={fileRef}
        hidden
        {...fileProps}
        onChange={handleFileSelection}
        onClick={(event) => {
          event.currentTarget.value = '';
        }}
        spellCheck={false}
      />
      {children}
    </Flex>
  );
};

export const FileUpload = (props: FileUploadProps) => {
  const styles = useMultiStyleConfig('FileUpload', props);
  const defaultRenderer = (file: FileWithExtraProps) => {
    return (
      <Flex sx={{ ...styles.FileCard }}>
        <Flex sx={{ ...styles.Title }}>
          {file.name}
          <Text sx={{ ...styles.Description }}>{bytesToSize(file.size)}</Text>
        </Flex>
      </Flex>
    );
  };
  const {
    files = [],
    fileProps,
    uploadIcon,
    uploadIconProps,
    label = 'Drag and drop or Click here to add Files',
    rootProps,
    containerProps,
    onFilesAdded,
    showUploadedFiles = true,
    selectedFilesRenderer = defaultRenderer,
    paginationProps,
    children,
    uploadedFilesContainer,
    uploadedFilesTitle,
    totalSizeTextProps,
  } = props;

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(5);
  const totalFiles = useMemo(() => files.length, [files]);
  const paginatedFiles = useMemo(
    () => files.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [currentPage, pageSize, files],
  );
  const defaultPaginationProps: PaginationProps = {
    pageSize: pageSize,
    current: currentPage,
    total: totalFiles,
    pageSizeOptions: [10, 20, 50, 100],
    onChange(page, _pageSize) {
      setCurrentPage(page);
    },
    onShowSizeChange(_page, size) {
      setPageSize(size);
    },
  };
  const mergedPaginationProps = {
    ...defaultPaginationProps,
    ...paginationProps,
  };
  const totalSize = useMemo(() => {
    let total = 0;
    files.map((file) => (total += file.size));
    return total;
  }, [files]);

  return (
    <Flex sx={{ ...styles.Root, ...rootProps }}>
      <Dragger
        containerProps={containerProps}
        onFilesAdded={onFilesAdded}
        fileProps={fileProps}
        files={files}
        bordered
      >
        <>
          {uploadIcon ? (
            uploadIcon
          ) : (
            <Icon
              as={AiOutlineCloudUpload}
              sx={{ ...styles.UploadIconProps }}
              {...uploadIconProps}
            />
          )}
          <Text>{label}</Text>
        </>
      </Dragger>
      {showUploadedFiles && files.length > 0 && (
        <Flex
          sx={{ ...styles.UploadedFilesContainer, ...uploadedFilesContainer }}
        >
          <Flex sx={{ ...styles.UploadedFilesTitle, ...uploadedFilesTitle }}>
            Selected Files
            <Text sx={{ ...styles.TotalSizeText, ...totalSizeTextProps }}>
              Total Size: {bytesToSize(totalSize)}
            </Text>
          </Flex>
          {paginatedFiles.map((file) => selectedFilesRenderer(file))}
        </Flex>
      )}
      <Pagination {...mergedPaginationProps} />
      {children}
    </Flex>
  );
};

FileUpload.Dragger = Dragger;
